// src/components/Dashboard/Cards/PersistencePredictionCard.jsx
import React, { useState, useEffect, useCallback } from 'react';
// import { getFirestore, collection, getDocs } from 'firebase/firestore'; // For real data
// import { useAuth } from '../../../../contexts/AuthContext'; // For real data
import DashboardCard from './DashboardCard';

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { toast } from 'react-toastify';

import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';

ChartJS.register(ArcElement, Tooltip, Legend, Title);

const PersistencePredictionCard = ({ onHide }) => {
  // const { currentUser } = useAuth();
  // const db = getFirestore();

  const [persistenceCounts, setPersistenceCounts] = useState({
    veryLow: 12,
    low: 18,
    moderate: 67,
    high: 233,
    veryHigh: 37,
  });
  const [isLoading, setIsLoading] = useState(true);

  const [studentsInNeed, setStudentsInNeed] = useState(63);
  const [studentsAddressed, setStudentsAddressed] = useState(40);

  const [weeklyImprovement, setWeeklyImprovement] = useState(14);
  const [weeklyDecline, setWeeklyDecline] = useState(25);
  const [averageRiskFactor, setAverageRiskFactor] = useState('Moderate');
  const [interventionsTriggered, setInterventionsTriggered] = useState(29);

  const getRiskFactorColor = (riskFactor) => {
    switch (riskFactor.toLowerCase()) {
      case 'very high':
        return 'text-red-600';
      case 'high':
        return 'text-orange-600';
      case 'moderate':
        return 'text-yellow-600';
      case 'low':
      case 'very low':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const getInterventionsColor = (count) => {
    if (count >= 50) return 'text-red-600';
    if (count >= 30) return 'text-orange-600';
    if (count >= 10) return 'text-yellow-600';
    return 'text-green-600';
  };

  const fetchPersistenceData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fake data
      const fixedDistribution = {
        veryLow: 12,
        low: 18,
        moderate: 67,
        high: 233,
        veryHigh: 37,
      };
      setPersistenceCounts(fixedDistribution);

      setStudentsInNeed(63);
      setStudentsAddressed(40);
      setWeeklyImprovement(14);
      setWeeklyDecline(25);
      setAverageRiskFactor('Moderate');
      setInterventionsTriggered(29);

      // For real data, replace with your API/Firestore logic
    } catch (err) {
      console.error('[PersistencePredictionCard] Error:', err);
      toast.error('Failed to fetch persistence data.');
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchPersistenceData();
  }, [fetchPersistenceData]);

  const totalStudents = 367; // Fixed total
  const addressedPercentage = studentsInNeed
    ? Math.round((studentsAddressed / studentsInNeed) * 100)
    : 0;

  // Donut Chart config
  const chartData = {
    labels: ['Very Low', 'Low', 'Moderate', 'High', 'Very High'],
    datasets: [
      {
        data: [
          persistenceCounts.veryLow,
          persistenceCounts.low,
          persistenceCounts.moderate,
          persistenceCounts.high,
          persistenceCounts.veryHigh,
        ],
        backgroundColor: [
          '#e11d48', // Very Low - red
          '#fb923c', // Low - orange
          '#facc15', // Moderate - yellow
          '#4ade80', // High - green
          '#38bdf8', // Very High - blue
        ],
        hoverOffset: 4,
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: { display: false },
      tooltip: {
        // Ensure the tooltip is rendered in a way that doesn't clip
        // (You might also need to ensure the parent container has overflow: visible)
        callbacks: {
          label: (ctx) => {
            const label = ctx.label || '';
            const value = ctx.raw || 0;
            return `${label}: ${value}`;
          },
        },
      },
    },
  };

  return (
    <DashboardCard
      title={<span className="font-medium">Predictive Insights</span>}
      onHide={onHide}
      isLoading={isLoading}
    >
      {!isLoading && (
        <div>
          {/* Main row: left content + right column of mini-cards */}
          <div className="flex flex-col md:flex-row items-stretch justify-between gap-3">
            {/* LEFT SECTION */}
            <div className="flex-1 flex flex-col md:flex-row gap-1">
              {/* Donut & Legend */}
              <div className="flex flex-col sm:flex-row items-start gap-3 overflow-visible bg-white dark:bg-gray-800
                           border border-gray-300 dark:border-gray-700
                           rounded-lg p-2 flex flex-col justify-between
                           relative transition-all duration-300">
                {/* Donut */}
                <div className="mt-2 relative w-32 h-32 flex-shrink-0 overflow-visible">
                  <Doughnut data={chartData} options={chartOptions} />
                  {/* Center label => total students */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-sm font-semibold">
                      {totalStudents}
                    </span>
                    <span className="text-xs text-gray-600">Students</span>
                  </div>
                </div>

                {/* Legend */}
                <div className="mt-2 min-w-[13.5vw] text-xs sm:text-sm">
                  <div className="font-semibold mb-1">
                    Prediction Distribution | Fall 2025 - Spring
                  </div>
                  <div className="space-y-1">
                    {[
                      {
                        label: 'Very Low',
                        count: persistenceCounts.veryLow,
                        color: 'bg-red-500',
                      },
                      {
                        label: 'Low',
                        count: persistenceCounts.low,
                        color: 'bg-orange-400',
                      },
                      {
                        label: 'Moderate',
                        count: persistenceCounts.moderate,
                        color: 'bg-yellow-400',
                      },
                      {
                        label: 'High',
                        count: persistenceCounts.high,
                        color: 'bg-green-400',
                      },
                      {
                        label: 'Very High',
                        count: persistenceCounts.veryHigh,
                        color: 'bg-blue-400',
                      },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className="flex justify-between items-center"
                      >
                        <div className="flex items-center">
                          <span
                            className={`w-3 h-3 ${item.color} rounded-full mr-2`}
                          />
                          <span>{item.label}</span>
                        </div>
                        <span>{item.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Students in Need & Progress */}
              <div className="min-w-[18vw] flex flex-col items-start md:items-end bg-white dark:bg-gray-800
                           border border-gray-300 dark:border-gray-700
                           rounded-lg p-2 flex flex-col justify-between
                           relative transition-all duration-300">
                <div className="mb-1 flex items-center space-x-1 text-red-600">
                  <ArrowDownIcon className="w-4 h-4" />
                  {/* Increase the font size here */}
                  <span className="text-2xl font-bold">{studentsInNeed}</span>
                </div>
                <p className="text-xs text-gray-500 mb-1">
                  Students experienced a 5% or more drop this week in their
                  persistence prediction.&nbsp;
                  <a href="#details" className="text-blue-600 underline">
                    View details
                  </a>
                </p>

                {/* Addressed Progress */}
                <div className="w-full sm:w-56 mb-1">
                  <div className="text-xs font-semibold mb-1">
                    Addressed Progress
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full"
                      style={{ width: `${addressedPercentage}%` }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {studentsAddressed} of {studentsInNeed} addressed (
                    {addressedPercentage}%)
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT SECTION: 4 mini-cards */}
            <div className="flex flex-col gap-2 max-w-[18vw] md:w-64 text-xs sm:text-sm">
              {/* Weekly Improvement */}
              <div className="flex flex-row items-center justify-between p-2 border rounded">
                <span className="font-semibold">Weekly Improvement</span>
                <div className="flex items-center space-x-1">
                  <ArrowUpIcon className="w-4 h-4 text-green-600" />
                  <span className="text-green-600 font-medium">
                    {weeklyImprovement} Students
                  </span>
                </div>
              </div>

              {/* Weekly Decline */}
              <div className="flex flex-row items-center justify-between p-2 border rounded">
                <span className="font-semibold">Weekly Decline</span>
                <div className="flex items-center space-x-1">
                  <ArrowDownIcon className="w-4 h-4 text-red-600" />
                  <span className="text-red-600 font-medium">
                    {weeklyDecline} Students
                  </span>
                </div>
              </div>

              {/* Average Risk Factor */}
              <div className="flex flex-row items-center justify-between p-2 border rounded">
                <span className="font-semibold">Average Risk Factor</span>
                <span
                  className={`font-medium ${getRiskFactorColor(
                    averageRiskFactor
                  )}`}
                >
                  {averageRiskFactor}
                </span>
              </div>

              {/* Interventions Triggered */}
              <div className="flex flex-row items-center justify-between p-2 border rounded">
                <span className="font-semibold">Interventions Triggered</span>
                <span
                  className={`font-medium ${getInterventionsColor(
                    interventionsTriggered
                  )}`}
                >
                  {interventionsTriggered}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardCard>
  );
};

export default PersistencePredictionCard;
